import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Truck, Calendar, MessageCircle, ArrowLeft, User, Stethoscope, Share2, AlertTriangle, CheckCircle2, Sparkles, QrCode, Eye } from "lucide-react";
import { toast } from "sonner";
import { isNew } from "@/lib/dates";
import FavoriteButton from "@/components/FavoriteButton";
import QRCodeDialog from "@/components/QRCodeDialog";
import { useAuth } from "@/context/AuthContext";

function expirationInfo(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(dateStr + "T00:00:00");
  if (isNaN(exp.getTime())) return null;
  const days = Math.round((exp - today) / 86400000);
  if (days < 0) return { days, level: "expired", text: `Caducó hace ${Math.abs(days)} día(s)`, className: "bg-rose-100 text-rose-900 border-rose-200", icon: AlertTriangle };
  if (days <= 30) return { days, level: "critical", text: `Caduca en ${days} día(s)`, className: "bg-rose-100 text-rose-900 border-rose-200", icon: AlertTriangle };
  if (days <= 90) return { days, level: "warn", text: `Caduca en ${days} días`, className: "bg-amber-100 text-amber-900 border-amber-200", icon: AlertTriangle };
  return { days, level: "ok", text: `Caduca en ${days} días`, className: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 };
}

export default function ListingDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setItem(r.data)).finally(() => setLoading(false));
  }, [id]);

  // Track scans from QR or share
  useEffect(() => {
    const src = searchParams.get("src");
    if (src === "qr" || src === "share") {
      api.post(`/listings/${id}/track-scan`, { source: src }).catch(() => {});
    }
  }, [id, searchParams]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-slate-500">Cargando…</div>;
  if (!item) return <div className="min-h-[60vh] grid place-items-center text-slate-500">No se encontró la publicación.</div>;

  const photos = [
    { key: "photo_front", label: "Frente" },
    { key: "photo_back", label: "Posterior" },
    { key: "photo_expiration", label: "Caducidad" },
    { key: "photo_contents", label: "Contenido" },
  ];

  const total = item.base_cost + (item.delivery_cost || 0);
  const waNumber = item.whatsapp.replace(/[^\d]/g, "");
  const waText = encodeURIComponent(`Hola, vi tu publicación de ${item.medicine_name} en Medicamentos_CiudadEnfermeria. ¿Sigue disponible?`);
  const waLink = `https://wa.me/${waNumber}?text=${waText}`;
  const expInfo = expirationInfo(item.expiration_date);

  const shareUrl = `${window.location.origin}/medicamento/${item.id}?src=share`;
  const shareText = `${item.medicine_name} · ${item.total_units} ${item.unit_label} · $${item.base_cost.toFixed(0)} MXN · Zona ${item.zone}`;
  const shareWa = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;

  const onShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: item.medicine_name, text: shareText, url: shareUrl });
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Enlace copiado");
    } catch {
      window.open(shareWa, "_blank");
    }
  };

  return (
    <div className="bg-slate-50 min-h-[80vh] pb-28">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Link to="/medicamentos" className="inline-flex items-center text-blue-700 hover:text-blue-900 text-sm font-medium mb-4" data-testid="detail-back-link">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver a la lista
        </Link>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <Card className="rounded-2xl overflow-hidden border border-slate-200 bg-white">
              <div className="aspect-[4/3] bg-slate-100">
                <img src={item[photos[activePhoto].key]} alt={photos[activePhoto].label} className="w-full h-full object-contain bg-white" />
              </div>
              <div className="grid grid-cols-4 gap-2 p-3 border-t border-slate-100">
                {photos.map((p, idx) => (
                  <button
                    key={p.key}
                    onClick={() => setActivePhoto(idx)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${idx === activePhoto ? "border-blue-600" : "border-transparent"}`}
                    data-testid={`detail-thumb-${idx}`}
                  >
                    <img src={item[p.key]} alt={p.label} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card className="p-5 sm:p-6 rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-blue-100 text-blue-900 hover:bg-blue-100">{item.category}</Badge>
                {isNew(item.created_at) && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-600 text-white text-[11px] font-bold shadow-sm" data-testid="detail-new-badge">
                    <Sparkles className="w-3 h-3" /> NUEVO
                  </span>
                )}
              </div>
              <h1 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-blue-950" data-testid="detail-name">{item.medicine_name}</h1>
              <p className="mt-1 text-slate-600">{item.total_units} {item.unit_label} disponibles</p>

              <div className="mt-5 pt-5 border-t border-slate-100 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Costo base</span>
                  <span className="font-semibold text-slate-900">${item.base_cost.toFixed(2)} MXN</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 flex items-center gap-1.5"><Truck className="w-4 h-4"/>Entrega</span>
                  <span className="font-semibold text-slate-900">{item.delivery_cost > 0 ? `$${item.delivery_cost.toFixed(2)} MXN` : "A convenir"}</span>
                </div>
                <div className="flex items-center justify-between text-base pt-2 border-t border-dashed">
                  <span className="font-medium text-slate-700">Total con entrega</span>
                  <span className="font-bold text-blue-900 text-xl">${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-slate-100 space-y-2 text-sm text-slate-700">
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-600"/>Zona: <strong>{item.zone}</strong></div>
                {item.expiration_date && (
                  <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-600"/>Caducidad: <strong>{item.expiration_date}</strong></div>
                )}
                <div className="flex items-center gap-2"><User className="w-4 h-4 text-blue-600"/>Publica: <strong>{item.created_by_name}</strong></div>
              </div>

              {expInfo && (
                <div className={`mt-4 rounded-xl border px-3 py-2.5 flex items-center gap-2 text-sm font-medium ${expInfo.className}`} data-testid="detail-expiration-alert">
                  <expInfo.icon className="w-4 h-4 shrink-0" />
                  <span>{expInfo.text}</span>
                  {expInfo.level === "critical" && (
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-rose-600 text-white">Verificar antes de comprar</span>
                  )}
                  {expInfo.level === "expired" && (
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-rose-700 text-white">NO COMPRAR</span>
                  )}
                </div>
              )}

              {item.description && (
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-700 mb-1">Notas</p>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{item.description}</p>
                </div>
              )}

              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:flex mt-6 h-14 w-full rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold items-center justify-center btn-press shadow-md"
                data-testid="detail-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" /> Contactar por WhatsApp
              </a>

              <Button
                type="button"
                variant="outline"
                onClick={onShare}
                className="hidden lg:flex mt-3 h-12 w-full rounded-xl border-2 border-blue-200 text-blue-700 hover:bg-blue-50 btn-press"
                data-testid="detail-share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" /> Compartir publicación
              </Button>

              <div className="hidden lg:block mt-3">
                <FavoriteButton listingId={item.id} variant="pill" className="w-full" />
              </div>

              {(user && (user.role === "admin" || user.id === item.created_by)) && (
                <div className="hidden lg:block mt-3" data-testid="detail-owner-qr">
                  <QRCodeDialog
                    listingId={item.id}
                    medicineName={item.medicine_name}
                    price={item.base_cost?.toFixed(0)}
                    zone={item.zone}
                    trigger={
                      <Button variant="outline" className="w-full h-12 rounded-xl border-2 border-blue-200 text-blue-700 hover:bg-blue-50">
                        <QrCode className="w-4 h-4 mr-2" /> QR para imprimir / WhatsApp
                      </Button>
                    }
                  />
                  {(item.qr_scans || item.share_scans) && (
                    <div className="mt-2 text-xs text-slate-500 flex items-center justify-center gap-3">
                      <span className="inline-flex items-center gap-1"><QrCode className="w-3 h-3"/> {item.qr_scans || 0} escaneos QR</span>
                      {item.share_scans > 0 && <span className="inline-flex items-center gap-1"><Share2 className="w-3 h-3"/> {item.share_scans} desde compartir</span>}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Cross-sell: nursing service */}
            <Card className="mt-4 p-5 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white" data-testid="detail-service-cta">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-blue-600 text-white grid place-items-center shrink-0">
                  <Stethoscope className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-semibold text-blue-950 text-base">
                    ¿También necesitas que alguien lo aplique?
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Enfermeras tituladas de Ciudad Enfermería pueden ayudarte con la aplicación,
                    curaciones y cuidado del paciente en casa.
                  </p>
                  <Link to="/servicios" className="inline-block mt-3">
                    <Button className="bg-blue-600 hover:bg-blue-700 h-11 rounded-xl" data-testid="detail-service-btn">
                      Solicitar servicio de enfermería
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Sticky WhatsApp + share button mobile */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 p-3 flex gap-2">
        <a
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-14 flex-1 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold items-center justify-center shadow-md"
          data-testid="detail-whatsapp-btn-mobile"
        >
          <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp
        </a>
        <Button
          type="button"
          onClick={onShare}
          variant="outline"
          className="h-14 w-14 rounded-xl border-2 border-blue-200 text-blue-700 hover:bg-blue-50 shrink-0"
          data-testid="detail-share-btn-mobile"
          aria-label="Compartir"
        >
          <Share2 className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
